import { type MouseEvent } from 'react';

import { useHiveRenderingMetadata } from './use-hive-rendering-metadata.js';
import { useHiveTileBestFit } from './use-hive-tile-best-fit.js';
import { useHoneycombData } from './use-honeycomb-data.js';
import { useHoneycombShape } from './use-honeycomb-shape.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  useSetStateOverlay,
  useSetStateTooltip,
  useToggleStatePinnedTooltip,
} from '../store/store.js';
import { findClosestTileStrategy } from '../utils/find-closest-tile.js';

export const useOverlayEvents = () => {
  const setOverlayState = useSetStateOverlay();
  const setTooltipState = useSetStateTooltip();
  const toggleTooltipPinnedState = useToggleStatePinnedTooltip();

  const honeycombData = useHoneycombData();
  const { tilesCenters, renderMetadata } = useHiveRenderingMetadata();
  const { tileWidth } = useHiveTileBestFit();
  const shape = useHoneycombShape();
  const handleMouseEnter = () => {
    setOverlayState({
      mouseInBounds: true,
    });
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const { clientX, clientY, currentTarget } = e;
    const { top, left } = (currentTarget as Element).getBoundingClientRect();

    const position = {
      x: clientX,
      y: clientY,
    };

    const relativePosition = {
      x: position.x - left,
      y: position.y - top,
    };

    const closestTile = findClosestTileStrategy[shape](
      tileWidth,
      tilesCenters,
      relativePosition,
    );

    setTooltipState({
      position,
      visible: closestTile !== undefined,
      tileData:
        closestTile !== undefined ? honeycombData[closestTile] : undefined,
      colorMetadata:
        closestTile !== undefined
          ? safeGet(renderMetadata, honeycombData[closestTile])
          : undefined,
    });
  };

  const handleMouseLeave = () => {
    setOverlayState({
      mouseInBounds: false,
    });
  };

  const handleMouseClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    toggleTooltipPinnedState();
  };

  return {
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    handleMouseClick,
  };
};
